import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Row,
  Col,
  Statistic,
  Descriptions,
  Typography,
  Tabs,
  Alert,
  Badge,
  Progress
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  AuditOutlined,
  UserOutlined,
  FileTextOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

// Create axios instance for JWT authentication (for admin endpoints)
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '/api',
  timeout: 10000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Just log the error without redirecting
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Search } = Input;
const { TabPane } = Tabs;

const MerchantApproval = () => {
  const [loading, setLoading] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [allCounts, setAllCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    inactive: 0,
    all: 0
  });
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  const [reviewForm] = Form.useForm();

  useEffect(() => {
    fetchMerchants();
  }, [activeTab]);

  // Fetch all counts for badges on component mount and set up periodic refresh
  useEffect(() => {
    fetchAllCounts();

    // Refresh counts every 30 seconds to keep badges updated
    const interval = setInterval(fetchAllCounts, 30000);

    // Also refresh when window gains focus (user returns to tab)
    const handleFocus = () => {
      fetchAllCounts();
      fetchMerchants(); // Also refresh merchant data
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchAllCounts = async () => {
    try {
      // Use merchant counts endpoint
      const response = await adminApi.get('/members/badge/merchant-counts');

      if (response.data && response.data.counts) {
        const apiCounts = response.data.counts;
        console.log('Merchant counts from API:', apiCounts);

        const counts = {
          pending: apiCounts.pending || 0,
          approved: apiCounts.approved || 0,
          rejected: apiCounts.rejected || 0,
          inactive: apiCounts.inactive || 0,
          all: apiCounts.all || 0
        };

        console.log('Final counts to set:', counts);
        setAllCounts(counts);
      } else {
        // Set all counts to 0 if no data available
        const emptyCounts = {
          pending: 0,
          approved: 0,
          rejected: 0,
          inactive: 0,
          all: 0
        };
        console.log('No counts data available, setting to zero');
        setAllCounts(emptyCounts);
      }

    } catch (error) {
      console.error('Error fetching all counts:', error);

      // Set all counts to 0 on error
      const errorCounts = {
        pending: 0,
        approved: 0,
        rejected: 0,
        inactive: 0,
        all: 0
      };

      setAllCounts(errorCounts);
      message.error('Failed to fetch merchant counts');
    }
  };

  const fetchMerchants = async (searchTerm = '', page = 1) => {
    try {
      setLoading(true);

      let apiUrl = '/members';
      let params = {
        page: page,
        limit: pagination.pageSize,
        search: searchTerm
      };

      // Set API endpoint and params based on active tab
      if (activeTab === 'pending') {
        apiUrl = '/members/pending-approval';
      } else if (activeTab === 'approved') {
        params.kyc_status = 'verified';
        params.role = 'merchant';
      } else if (activeTab === 'rejected') {
        params.kyc_status = 'rejected';
        params.role = 'merchant';
      } else if (activeTab === 'inactive') {
        params.status = 'inactive';
        params.role = 'merchant';
      } else if (activeTab === 'all') {
        params.role = 'merchant';
      }

      const response = await adminApi.get(apiUrl, { params });

      let merchantsData = response.data.members || [];
      console.log('Raw API response for', activeTab, 'tab:', merchantsData);
      console.log('API response structure:', response.data);

      // Transform the data to match the expected format
      const transformedData = merchantsData.map(merchant => {
        const transformed = {
          id: merchant.id,
          member_id: merchant.member_id || merchant.id,
          full_name: merchant.full_name,
          email: merchant.email,
          phone: merchant.phone,
          business_type: merchant.kyc_level || 'basic',
          business_category: merchant.business_category || 'General',
          kyc_status: merchant.kyc_status,
          status: merchant.status,
          registration_date: merchant.created_at,
          documents_count: merchant.documents_count || 0,
          documents_verified: merchant.documents_verified || 0
        };
        console.log('Transformed merchant:', transformed);
        return transformed;
      });

      // Update pagination if available
      if (response.data.pagination) {
        setPagination({
          current: response.data.pagination.currentPage,
          pageSize: response.data.pagination.itemsPerPage,
          total: response.data.pagination.totalItems
        });
      }

      // No additional filtering needed - backend handles filtering based on active tab
      console.log('Final transformed data for', activeTab, 'tab:', transformedData);
      console.log('Number of merchants:', transformedData.length);
      setMerchants(transformedData);
    } catch (error) {
      console.error('Error fetching merchants:', error);
      message.error('Failed to fetch merchants data');
      setMerchants([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (merchant) => {
    setSelectedMerchant(merchant);
    setDetailModalVisible(true);
  };

  const handleReview = (merchant, action) => {
    setSelectedMerchant(merchant);
    setReviewModalVisible(true);
    reviewForm.setFieldsValue({
      action,
      notes: '',
      merchant_id: merchant.member_id
    });
  };

  const handleReviewSubmit = async (values) => {
    try {
      setLoading(true);
      const { action, notes, merchant_id } = values;

      // Call the real API
      await adminApi.patch(`/members/${merchant_id}/approve`, {
        action,
        notes
      });

      message.success(
        action === 'approve'
          ? 'Merchant approved successfully! They can now login to the merchant dashboard.'
          : 'Merchant rejected successfully!'
      );

      setReviewModalVisible(false);
      reviewForm.resetFields();
      fetchMerchants();
      fetchAllCounts(); // Refresh badge counts after approval/rejection
    } catch (error) {
      console.error('Review error:', error);
      message.error(
        error.response?.data?.error || 'Failed to process review. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      active: 'blue',
      blocked: 'red'
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Merchant ID',
      dataIndex: 'member_id',
      key: 'member_id',
      width: 120,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Business Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => <Text>{text}</Text>
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <MailOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            <Text style={{ fontSize: 12 }}>{record.email}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PhoneOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            <Text style={{ fontSize: 12 }}>{record.phone}</Text>
          </div>
        </div>
      )
    },
    {
      title: 'Business Info',
      key: 'business_info',
      width: 150,
      render: (_, record) => (
        <div>
          <Tag color="blue" style={{ marginBottom: 4 }}>
            {record.business_type}
          </Tag>
          <div>
            <Text style={{ fontSize: 12, color: '#666' }}>
              {record.business_category}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'KYC Progress',
      key: 'kyc_progress',
      width: 150,
      render: (_, record) => {
        const percent = record.documents_count > 0
          ? Math.round((record.documents_verified / record.documents_count) * 100)
          : 0;
        return (
          <div>
            <Progress
              percent={percent}
              size="small"
              strokeColor={{
                '0%': '#ff7a45',
                '100%': '#52c41a',
              }}
            />
            <div style={{ marginTop: 4 }}>
              <Text style={{ fontSize: 12 }}>
                {record.documents_verified}/{record.documents_count} documents
              </Text>
            </div>
          </div>
        );
      }
    },
    {
      title: 'KYC Status',
      dataIndex: 'kyc_status',
      key: 'kyc_status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={
          status === 'approved' ? <CheckCircleOutlined /> :
          status === 'rejected' ? <CloseCircleOutlined /> :
          <ExclamationCircleOutlined />
        }>
          {status?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Registration Date',
      dataIndex: 'registration_date',
      key: 'registration_date',
      width: 120,
      render: (date) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CalendarOutlined style={{ marginRight: 4, color: '#666' }} />
            <Text style={{ fontSize: 12 }}>
              {dayjs(date).format('DD MMM YYYY')}
            </Text>
          </div>
          <Text style={{ fontSize: 11, color: '#999' }}>
            {dayjs(date).format('HH:mm')}
          </Text>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          />
          <Button
            type="text"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => message.info('Document viewer coming soon')}
          />
          {(record.kyc_status === 'pending' || record.kyc_status === 'unverified') && (
            <>
              <Button
                type="text"
                size="small"
                icon={<CheckCircleOutlined />}
                style={{ color: '#52c41a' }}
                onClick={() => handleReview(record, 'approve')}
              />
              <Button
                type="text"
                size="small"
                icon={<CloseCircleOutlined />}
                style={{ color: '#ff4d4f' }}
                onClick={() => handleReview(record, 'reject')}
              />
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
            <AuditOutlined style={{ marginRight: 12, color: '#1890ff' }} />
            Merchant Approval
          </Title>
          <Text type="secondary">
            Review and approve pending merchant applications
          </Text>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Pending Approval"
                value={allCounts.pending}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Approved Merchants"
                value={allCounts.approved}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Rejected Merchants"
                value={allCounts.rejected}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Applications"
                value={allCounts.all}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Search
              placeholder="Search by merchant ID, name, or email"
              allowClear
              style={{ width: 300 }}
              onSearch={(value) => fetchMerchants(value)}
              onChange={(e) => {
                if (!e.target.value) {
                  fetchMerchants(''); // Clear search when input is empty
                }
              }}
            />
          </Space>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchMerchants();
              fetchAllCounts();
            }}
            loading={loading}
          >
            Refresh
          </Button>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={
            <Badge count={allCounts.pending} size="small">
              <span>Pending Approval</span>
            </Badge>
          } key="pending" />
          <TabPane tab={
            <Badge count={allCounts.approved} size="small">
              <span>Approved</span>
            </Badge>
          } key="approved" />
          <TabPane tab={
            <Badge count={allCounts.rejected} size="small">
              <span>Rejected</span>
            </Badge>
          } key="rejected" />
          <TabPane tab={
            <Badge count={allCounts.inactive} size="small">
              <span>Inactive</span>
            </Badge>
          } key="inactive" />
          <TabPane tab={
            <Badge count={allCounts.all} size="small">
              <span>All Merchants</span>
            </Badge>
          } key="all" />
        </Tabs>

        <Table
          columns={columns}
          dataSource={merchants}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} merchants`,
            onChange: (page, pageSize) => {
              fetchMerchants('', page);
            },
            onShowSizeChange: (current, size) => {
              setPagination(prev => ({ ...prev, pageSize: size }));
              fetchMerchants('', 1);
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Merchant Details Modal */}
      <Modal
        title="Merchant Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedMerchant && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Merchant ID" span={2}>
              <Text strong>{selectedMerchant.member_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Business Name" span={2}>
              {selectedMerchant.full_name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              <MailOutlined style={{ marginRight: 8 }} />
              {selectedMerchant.email}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              <PhoneOutlined style={{ marginRight: 8 }} />
              {selectedMerchant.phone}
            </Descriptions.Item>
            <Descriptions.Item label="Business Type">
              <Tag color="blue">{selectedMerchant.business_type}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Business Category">
              {selectedMerchant.business_category}
            </Descriptions.Item>
            <Descriptions.Item label="KYC Status">
              <Tag color={getStatusColor(selectedMerchant.kyc_status)}>
                {selectedMerchant.kyc_status?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Registration Date">
              {dayjs(selectedMerchant.registration_date).format('DD MMM YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="KYC Progress" span={2}>
              <Progress
                percent={Math.round((selectedMerchant.documents_verified / selectedMerchant.documents_count) * 100)}
                format={() => `${selectedMerchant.documents_verified}/${selectedMerchant.documents_count} documents verified`}
              />
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Review Modal */}
      <Modal
        title="Review Application"
        open={reviewModalVisible}
        onCancel={() => setReviewModalVisible(false)}
        footer={null}
      >
        {selectedMerchant && (
          <Form
            form={reviewForm}
            layout="vertical"
            onFinish={handleReviewSubmit}
          >
            <Alert
              message={`Reviewing: ${selectedMerchant.full_name}`}
              description={`Merchant ID: ${selectedMerchant.member_id}`}
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              name="merchant_id"
              style={{ display: 'none' }}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="action"
              label="Decision"
              rules={[{ required: true, message: 'Please select an action' }]}
            >
              <Select size="large" placeholder="Select your decision">
                <Option value="approve">
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  Approve Application
                </Option>
                <Option value="reject">
                  <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                  Reject Application
                </Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="notes"
              label="Review Notes"
              rules={[{ required: true, message: 'Please provide review notes' }]}
            >
              <TextArea
                rows={4}
                placeholder="Enter detailed notes for your decision..."
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setReviewModalVisible(false)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  danger={reviewForm.getFieldValue('action') === 'reject'}
                >
                  {reviewForm.getFieldValue('action') === 'approve' ? 'Approve' : 'Reject'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default MerchantApproval;